import type {
  IFabricCuratedObservabilityStatusResponse,
  IFabricObservabilityDependency,
  IFabricRawObservabilityStatusResponse,
} from '@skystone/shared';
import type { ReactNode } from 'react';
import type { ColumnDef } from '@/components/ui';
import { Badge, Card, CardContent, CardHeader, CardTitle, DataTable } from '@/components/ui';

const TEXT_FILTER = { type: 'text' as const, placeholder: 'Filter...' };

type BadgeVariant = 'default' | 'secondary' | 'success' | 'warning' | 'error';

type DependencyGateStatus =
  | IFabricRawObservabilityStatusResponse['dependencyGate']['status']
  | IFabricCuratedObservabilityStatusResponse['dependencyGate']['status'];

export function getGateBadgeVariant(status: DependencyGateStatus): BadgeVariant {
  return status === 'passed' ? 'success' : 'error';
}

function getDependencyBadgeVariant(status: IFabricObservabilityDependency['status']): BadgeVariant {
  if (status === 'ready') {
    return 'success';
  }

  if (status === 'missing_watermark') {
    return 'warning';
  }

  return 'error';
}

export function formatDate(value: string | null): string {
  return value ?? 'Not available';
}

export function formatReasonCodes(reasonCodes: string[]): string {
  return reasonCodes.length > 0 ? reasonCodes.join(', ') : 'None';
}

export function formatScopeDescriptor(value: Record<string, unknown> | undefined): string {
  if (value === undefined) {
    return '—';
  }

  return JSON.stringify(value);
}

export function formatOptionalPrimitive(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number') {
    return String(value);
  }

  return '—';
}

export function createTextColumn<TData>(
  id: string,
  header: string,
  accessor: ColumnDef<TData>['accessor'],
  cell?: ColumnDef<TData>['cell'],
): ColumnDef<TData> {
  return {
    id,
    header,
    accessor,
    sortable: true,
    filter: TEXT_FILTER,
    cell,
  };
}

const dependencyColumns: ColumnDef<IFabricObservabilityDependency>[] = [
  createTextColumn<IFabricObservabilityDependency>('jobType', 'Job Type', 'jobType'),
  createTextColumn<IFabricObservabilityDependency>('entityType', 'Entity Type', 'entityType'),
  createTextColumn<IFabricObservabilityDependency>(
    'latestSuccessfulRunId',
    'Latest Successful Run ID',
    'latestSuccessfulRunId',
    (value) => formatOptionalPrimitive(value),
  ),
  createTextColumn<IFabricObservabilityDependency>(
    'latestWatermark',
    'Latest Watermark',
    'latestWatermark',
    (value) => formatOptionalPrimitive(value),
  ),
  {
    id: 'status',
    header: 'Status',
    accessor: 'status',
    sortable: true,
    filter: TEXT_FILTER,
    cell: (value) => {
      const statusValue = String(value) as IFabricObservabilityDependency['status'];
      return <Badge variant={getDependencyBadgeVariant(statusValue)}>{statusValue}</Badge>;
    },
  },
];

export interface ISyncSummaryCardProps {
  title: string;
  sourceLayer: string;
  asOf: string | null;
  dependencyGateStatus: DependencyGateStatus;
  reasonCodes: string[];
}

interface ISummaryMetric {
  label: string;
  value: ReactNode;
}

function SummaryMetric({ label, value }: Readonly<ISummaryMetric>) {
  return (
    <div>
      <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
      <p className="text-sm font-medium">{value}</p>
    </div>
  );
}

export function SyncSummaryCard({
  title,
  sourceLayer,
  asOf,
  dependencyGateStatus,
  reasonCodes,
}: Readonly<ISyncSummaryCardProps>) {
  const metrics: ISummaryMetric[] = [
    { label: 'Source Layer', value: sourceLayer },
    { label: 'As Of', value: formatDate(asOf) },
    {
      label: 'Dependency Gate',
      value: <Badge variant={getGateBadgeVariant(dependencyGateStatus)}>{dependencyGateStatus}</Badge>,
    },
    { label: 'Reason Codes', value: formatReasonCodes(reasonCodes) },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-3 pt-0 sm:grid-cols-2 lg:grid-cols-4">
        {metrics.map((metric) => (
          <SummaryMetric key={metric.label} label={metric.label} value={metric.value} />
        ))}
      </CardContent>
    </Card>
  );
}

export interface IDependencyGateDetailsCardProps {
  dependencies: IFabricObservabilityDependency[];
  emptyMessage: string;
}

export function DependencyGateDetailsCard({
  dependencies,
  emptyMessage,
}: Readonly<IDependencyGateDetailsCardProps>) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Dependency Gate Details</CardTitle>
      </CardHeader>
      <CardContent>
        <DataTable
          data={dependencies}
          columns={dependencyColumns}
          keyExtractor={(dependency) => `${dependency.jobType}:${dependency.entityType}`}
          enablePagination
          enableFilters
          emptyMessage={emptyMessage}
        />
      </CardContent>
    </Card>
  );
}

export { TEXT_FILTER };
